import LoginDialog from "../components/login/LoginDialog";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "./AnalyticsManager";
import { AwsCredentials } from "../models/AwsCredentials";
import { RegionsResponse, UnauthenticatedNewformaApiClient } from "./UnauthenticatedNewformaApiClient";
import { AuthUrlProvider } from "./AuthUrlProvider";
import { OfficeWrapper } from "./OfficeWrapper";
import { IdpConfiguration } from "../models/IdpConfiguration";
import { StorageWrapper } from "./StorageWrapper";
import { LocalStorageKeys } from "../models/StorageKeys";
import { Eula } from "../models/EulaModels";
import { OpenIDCredentials } from "../models/OpenIDCredentials";
import { Logger } from "./Logger";
import { ExpiredSessionError } from "../models/ExpiredSessionError";

export class Authenticator {
    onEulaRequired: ((isUpdate: boolean) => Promise<Eula | undefined>) | undefined;
    onAuthenticationError: ((errorMessage: string) => Promise<void>) | undefined;
    private refreshCredentialsPromise: Promise<AwsCredentials | null> | null = null;

    constructor(
        private loginDialog: LoginDialog,
        private storageWrapper: StorageWrapper,
        private apiClient: UnauthenticatedNewformaApiClient,
        private authUrlProvider: AuthUrlProvider,
        private officeWrapper: OfficeWrapper,
        private analyticsManager: AnalyticsManager,
        private logger: Logger
    ) {}

    getRegions(twoLettersLanguage: string): Promise<RegionsResponse> {
        return this.apiClient.getRegions(twoLettersLanguage);
    }

    getStoredCredentials(): AwsCredentials | null {
        const credentials = this.storageWrapper.loadAuthStorage(LocalStorageKeys.awsCredentials);
        if (credentials) {
            try {
                const parsed = JSON.parse(credentials);
                if (parsed && parsed.expiration) {
                    parsed.expiration = new Date(parsed.expiration);
                }
                return parsed;
            } catch {
                return null;
            }
        }
        return null;
    }

    isUserLoggedIn(): boolean {
        return !!this.getStoredCredentials();
    }

    isValidUser(): boolean {
        const savedUser = this.storageWrapper.loadAuthStorage(LocalStorageKeys.currentLoggedEmail);
        if (savedUser !== this.officeWrapper.userProfileEmailAddress) {
            this.logger.warning(
                `User is invalid. Current user ${this.officeWrapper.userProfileEmailAddress}. Saved user ${savedUser}`
            );
            return false;
        }
        return true;
    }

    logoutFromApp(): void {
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.awsCredentials);
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.idToken);
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.currentLoggedEmail);
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.graphToken);
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.tokenExpiry);
        this.storageWrapper.removeAuthStorageItem(LocalStorageKeys.awsCredentialExpiry);
    }

    async getOpenIdCredentials(config: IdpConfiguration): Promise<OpenIDCredentials> {
        const authorizeUrl = await this.authUrlProvider.getAuthorizeUrl(config);
        const openIdCredentials = await this.loginDialog.openAuthDialog(authorizeUrl, false);
        this.storageWrapper.saveAuthStorage(LocalStorageKeys.idToken, openIdCredentials.idToken);
        this.storeTokenExpiry(openIdCredentials.idToken);
        return openIdCredentials;
    }

    async handleLogin(
        openIdCredentials: OpenIDCredentials,
        config: IdpConfiguration,
        eula: Eula | undefined,
        region?: string
    ): Promise<AwsCredentials> {
        const credentials = await this.apiClient.login(openIdCredentials, eula, region);
        this.storageWrapper.saveAuthStorage(LocalStorageKeys.awsCredentials, JSON.stringify(credentials));
        this.storeAwsCredentialExpiry(credentials);
        // Re-save idToken here to guard against a concurrent logoutFromApp() clearing it
        // between getOpenIdCredentials() and handleLogin()
        this.storageWrapper.saveAuthStorage(LocalStorageKeys.idToken, openIdCredentials.idToken);
        this.storageWrapper.saveAuthStorage(
            LocalStorageKeys.currentLoggedEmail,
            this.officeWrapper.userProfileEmailAddress
        );

        const token = this.storageWrapper.loadAuthStorage(LocalStorageKeys.idToken);
        if (!token) {
            if (this.onAuthenticationError) {
                await this.onAuthenticationError("AUTHENTICATION.COOKIE_ERROR");
            }
            this.logger.error(
                "Authenticator could not access local storage. Cookies MUST be enabled for this application to work."
            );
        }

        const loggedInUsersCompany = this.officeWrapper.userProfileEmailDomain;
        this.analyticsManager.recordEvent(
            AnalyticsCategoryType.UserData,
            AnalyticsActionType.Company,
            loggedInUsersCompany
        );
        this.analyticsManager.recordEvent(AnalyticsCategoryType.UserActions, AnalyticsActionType.Login);
        return credentials;
    }

    async refreshCredentials(): Promise<AwsCredentials | null> {
        // If we already have non-expired AWS credentials, don't refresh.
        // This avoids repeatedly invoking the dialog-based refreshToken flow.
        const storedCredentials = this.getStoredCredentials();
        if (storedCredentials && this.areAwsCredentialsValidForMs(storedCredentials, 2 * 60 * 1000)) {
            this.logger.info("Authenticator: Stored AWS credentials still valid; skipping refresh");
            return storedCredentials;
        }

        // If the user is logged out (no stored session markers), do not attempt a silent refresh.
        // This prevents refreshToken() from running on startup/background requests.
        const storedEmail = this.storageWrapper.loadAuthStorage(LocalStorageKeys.currentLoggedEmail);
        const storedIdToken = this.storageWrapper.loadAuthStorage(LocalStorageKeys.idToken);
        if (!storedEmail || !storedIdToken) {
            this.logger.info("Authenticator: No stored session (currentLoggedEmail/idToken missing); skipping refresh");
            throw new ExpiredSessionError();
        }

        // If the stored user doesn't match the current Outlook profile, treat as logged out.
        if (!this.isValidUser()) {
            this.logger.info("Authenticator: Stored user does not match current profile; skipping refresh");
            throw new ExpiredSessionError();
        }

        if (this.refreshCredentialsPromise) {
            return this.refreshCredentialsPromise;
        }
        this.logger.info(`Authenticator: In refreshCredentials`);

        // Cannot refresh without a user email (e.g. running outside Outlook)
        if (!this.officeWrapper.userProfileEmailAddress) {
            this.logger.warning("Authenticator: No user email available, cannot refresh credentials");
            throw new ExpiredSessionError();
        }

        try {
            this.refreshCredentialsPromise = this.createRefreshCredentialsPromise();
            return await this.refreshCredentialsPromise;
        } catch (error) {
            this.logoutFromApp();
            this.logger.error(`Authenticator: In refreshCredentials: ${error}`);
            throw new ExpiredSessionError();
        } finally {
            this.refreshCredentialsPromise = null;
        }
    }

    private async createRefreshCredentialsPromise(): Promise<AwsCredentials | null> {
        this.logger.info(`Authenticator: In createRefreshCredentialsPromise`);
        const refreshUrl = await this.authUrlProvider.getRefreshUrl();
        this.logger.info(`Authenticator: In createRefreshCredentialsPromise, refreshUrl: ${refreshUrl}`);

        let authDialogResult;
        let credentials = null;
        try {
            authDialogResult = await this.loginDialog.refreshToken(refreshUrl);
            this.logger.info(`Authenticator: authDialogResult received`);
            this.storageWrapper.saveAuthStorage(LocalStorageKeys.idToken, authDialogResult.idToken);
            this.storeTokenExpiry(authDialogResult.idToken);
            try {
                credentials = await this.apiClient.login(authDialogResult, undefined);
            } catch (error) {
                const errorType = (error as any).responseJSON?.type;
                if (this.onEulaRequired && (error as any).status === 400 && errorType?.includes("bad_request#eula_")) {
                    const isEulaUpdate = errorType.includes("bad_request#eula_update_confirmation_required");
                    const eula = await this.onEulaRequired(isEulaUpdate);
                    const afterEulaAuthDialogResult = await this.loginDialog.refreshToken(refreshUrl);
                    credentials = await this.apiClient.login(afterEulaAuthDialogResult, eula);
                } else {
                    throw error;
                }
            }
        } catch (error) {
            this.logger.error(`Authenticator: errored when receiving authDialogResult`);
            throw error;
        }

        this.storageWrapper.saveAuthStorage(LocalStorageKeys.awsCredentials, JSON.stringify(credentials));
        this.storeAwsCredentialExpiry(credentials);

        return credentials;
    }

    private areAwsCredentialsValidForMs(credentials: AwsCredentials, minValidityMs: number): boolean {
        try {
            const expirationRaw: any = (credentials as any)?.expiration;
            const expirationMs = new Date(expirationRaw).getTime();
            if (!Number.isFinite(expirationMs)) {
                return false;
            }
            const remainingMs = expirationMs - Date.now();
            return remainingMs > minValidityMs;
        } catch {
            return false;
        }
    }

    private storeAwsCredentialExpiry(credentials: AwsCredentials | null): void {
        try {
            if (credentials?.expiration) {
                const expiryDate = new Date(credentials.expiration);
                const expiry = `${expiryDate.toISOString()} [${expiryDate.toLocaleString()}]`;
                this.storageWrapper.saveAuthStorage(LocalStorageKeys.awsCredentialExpiry, expiry);
                this.logger.infoStyle(`Authenticator: AWS credentials expire at ${expiry}`, "green");
            }
        } catch (error) {
            this.logger.error("Authenticator.storeAwsCredentialExpiry: failed", error);
        }
    }

    private storeTokenExpiry(token: string): void {
        try {
            const parts = token.split(".");
            if (parts.length !== 3) {
                this.logger.warning("Authenticator.storeTokenExpiry: token is not a valid JWT");
                return;
            }
            const payload = JSON.parse(atob(parts[1]));
            if (payload.exp) {
                const expiryDateObj = new Date(payload.exp * 1000);
                const expiryDate = `${expiryDateObj.toISOString()} [${expiryDateObj.toLocaleString()}]`;
                this.storageWrapper.saveAuthStorage(LocalStorageKeys.tokenExpiry, expiryDate);
                this.logger.infoStyle(`Authenticator: JWT token expires at ${expiryDate}`, "green");
            } else {
                this.logger.warning("Authenticator.storeTokenExpiry: no exp claim found in token");
            }
        } catch (error) {
            this.logger.error("Authenticator.storeTokenExpiry: failed to decode token", error);
        }
    }
}
